/**
 * Content Schedule Filter (V059)
 *
 * Decides if a PlaylistItem (CONTENT) can play RIGHT NOW based on its own
 * day-of-week + hour window. Reuses primitives from `scheduleParser.ts` so
 * Tizen hour=24 normalization is preserved (vide regras-temporais-player.md).
 *
 * Architecture (decisão Renato — Socratic Gate da feature):
 *
 *   Server (MediaSyncService.isContentScheduleStaticallyImpossible):
 *     descarta config que NUNCA pode tocar (ADVANCED com todos dias null,
 *     SIMPLE com daysOfWeek=[] e horário não-default). Payload do sync
 *     enxuto, hash estável.
 *
 *   Cliente (este arquivo):
 *     filtra HORA ATUAL local do device. Compõe AND com Campanha
 *     (`isCampaignActive` → `isScheduleActive`) e Playlist Item
 *     (próprio startAt/endAt). Se Conteúdo está fora da janela, é tratado
 *     igual a campaign inactive: não toca, mas associação no DB continua.
 *
 * UX defensiva (admin):
 *   - Lista de Conteúdos: badge "🕒 Fora de janela agora" (calculado a cada min).
 *   - ContentDetailPanel: linha "Próxima janela: segunda 08:00".
 *   - Player detail (associações): item disabled + tooltip
 *     "fora do horário, só habilita em seg-sex 08-18h".
 *   - Troubleshooting panel: log "[scheduler] X filtrado por janela própria".
 */

import { PlaylistItem } from '../types';
import { matchesWeather } from './weatherConditionMatcher';
import { weatherStore, type WeatherData } from '~/stores/weather.store';
import {
  getCurrentTimeInTimezone,
  isDayActive,
  isTimeInRange,
} from './scheduleParser';

/**
 * Mapping uppercase 3-letter weekday code → JS Date.getDay() index (0=Sun..6=Sat).
 * Used to translate the backend's "MON"/"TUE"/... into the numeric format
 * consumed by `isDayActive`.
 */
const WEEKDAY_CODE_TO_INDEX: Record<string, number> = {
  SUN: 0,
  MON: 1,
  TUE: 2,
  WED: 3,
  THU: 4,
  FRI: 5,
  SAT: 6,
};

const WEEKDAY_INDEX_TO_CODE: Record<number, string> = {
  0: 'SUN',
  1: 'MON',
  2: 'TUE',
  3: 'WED',
  4: 'THU',
  5: 'FRI',
  6: 'SAT',
};

/**
 * Default business timezone — same as campaign matcher (Brazil).
 * Could be passed per-content if multi-region support arrives, but for now
 * the rule is "tudo no horário da loja BR" (vide regras-temporais-player.md R1).
 */
const DEFAULT_TZ = 'America/Sao_Paulo';

export interface ContentScheduleDecision {
  /** true if content can play at `now` based on its own schedule. */
  isPlayable: boolean;
  /** Reason for filtering (debug log). null if isPlayable=true. */
  reason: string | null;
}

/**
 * Lowercases weekday codes coming from the legacy `daysOfWeek` field
 * (server emits "mon"/"tue"/... in lowercase via MediaSyncService).
 * The new `weeklySchedule` uses UPPERCASE keys (paridade com Campanha).
 */
function normalizeWeekdayCode(raw: string): string {
  return raw.trim().toUpperCase().slice(0, 3);
}

/**
 * 2026-06-12 — Validade interpretada no fuso LOCAL do device.
 *
 * <p>`validFrom`/`validUntil` são strings `YYYY-MM-DD` (sem hora/fuso).
 * Player em Manaus (GMT-4) interpreta "12/06" como "início do dia 12/06
 * Manaus" (= 04:00 UTC 12/06) e "fim do dia 12/06 Manaus" (= 04:00 UTC 13/06).
 * Resultado: player em qualquer fuso BR respeita o dia configurado pelo
 * operador BRT sem perder horas.</p>
 *
 * <p>Server tem margem assimétrica (Noronha pra validFrom, Acre pra
 * validUntil) — vide R0 em regras-temporais-player.md. Player nunca
 * recebe conteúdo "antes do dia em Noronha" nem "depois do dia em Acre",
 * então a filtragem aqui é o "afunilamento final" no fuso real do device.</p>
 */
export function isContentValidNow(
  item: PlaylistItem,
  now: Date = new Date(),
): ContentScheduleDecision {
  const { validFrom, validUntil } = item;
  if (!validFrom && !validUntil) {
    return { isPlayable: true, reason: null };
  }

  if (validFrom) {
    const start = parseDateAtLocalStartOfDay(validFrom);
    if (start && now < start) {
      return {
        isPlayable: false,
        reason: `validFrom ${validFrom} ainda no futuro (fuso local)`,
      };
    }
  }

  if (validUntil) {
    const end = parseDateAtLocalEndOfDay(validUntil);
    if (end && now > end) {
      return {
        isPlayable: false,
        reason: `validUntil ${validUntil} já passou (fuso local)`,
      };
    }
  }

  return { isPlayable: true, reason: null };
}

/**
 * Parse "YYYY-MM-DD" → Date(year, month-1, day, 0, 0, 0, 0) NO FUSO LOCAL.
 * Retorna null se a string for inválida (defensivo).
 */
function parseDateAtLocalStartOfDay(dateStr: string): Date | null {
  const parts = dateStr.split('-').map(Number);
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) {
    return null;
  }
  const [year, month, day] = parts;
  return new Date(year, month - 1, day, 0, 0, 0, 0);
}

/**
 * Parse "YYYY-MM-DD" → Date(year, month-1, day, 23, 59, 59, 999) NO FUSO LOCAL.
 */
function parseDateAtLocalEndOfDay(dateStr: string): Date | null {
  const parts = dateStr.split('-').map(Number);
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) {
    return null;
  }
  const [year, month, day] = parts;
  return new Date(year, month - 1, day, 23, 59, 59, 999);
}

/**
 * Decides whether a CONTENT (PlaylistItem.scheduleMode + .weeklySchedule
 * + .daysOfWeek + .startAt/.endAt) is currently playable.
 *
 * - No schedule info (all fields undefined/empty) → ALWAYS playable (legacy).
 * - SIMPLE: daysOfWeek (lowercase mon/tue/...) + startAt/endAt (24h "HH:mm").
 * - ADVANCED: weeklySchedule[CODE] → {startAt, endAt}; null entry = blocked.
 *
 * Reuses `isTimeInRange` (which normalizes Tizen hour=24 → 0) and `isDayActive`
 * from scheduleParser.ts — single source of truth pra evitar regressão.
 *
 * 2026-06-12 — EARLY RETURN no filtro de validUntil/validFrom (fuso LOCAL).
 * Player em Manaus/Acre respeita o dia configurado pelo operador sem perder
 * horas devido ao gap BRT → local. Vide `isContentValidNow`.
 */
export function isContentPlayable(
  item: PlaylistItem,
  now: Date = new Date(),
  weather: WeatherData | null = weatherStore.current,
): ContentScheduleDecision {
  // EARLY RETURN — validade no fuso LOCAL do device. Mais barato que
  // processar weeklySchedule/SIMPLE check.
  const validity = isContentValidNow(item, now);
  if (!validity.isPlayable) {
    return validity;
  }

  // V088 (2026-08-06) — condição de clima do CONTEÚDO (AND adicional).
  // Compõe com a regra da campanha (avaliada em campaignMatcher.isCampaignActive).
  // Regra vazia/ausente → passa. Regra setada + sem dado de clima → conservador
  // (não toca) — best-effort, FEATURE-WEATHER.md §6.7.
  // Vide regras-temporais-player.md R2/R4.
  if (!matchesWeather(item.weatherConditionContent, weather)) {
    return {
      isPlayable: false,
      reason: 'condição de clima do conteúdo não bate',
    };
  }

  const mode = item.scheduleMode;

  // ADVANCED mode — uses weeklySchedule
  if (mode === 'ADVANCED') {
    const weekly = item.weeklySchedule;
    if (!weekly || Object.keys(weekly).length === 0) {
      // ADVANCED sem schedule → bloqueado (consistente com server filter)
      return { isPlayable: false, reason: 'ADVANCED sem weeklySchedule' };
    }
    const { hours, minutes, dayOfWeek } = getCurrentTimeInTimezone(
      DEFAULT_TZ,
      now,
    );
    const todayCode = WEEKDAY_INDEX_TO_CODE[dayOfWeek];
    const dayConfig = weekly[todayCode];
    if (!dayConfig) {
      return {
        isPlayable: false,
        reason: `dia ${todayCode} não configurado em weeklySchedule`,
      };
    }
    try {
      const inRange = isTimeInRange(
        { hours, minutes },
        dayConfig.startAt,
        dayConfig.endAt,
      );
      return {
        isPlayable: inRange,
        reason: inRange
          ? null
          : `hora ${hours}:${minutes} fora de ${dayConfig.startAt}-${dayConfig.endAt} (${todayCode})`,
      };
    } catch (err) {
      // Parsing falhou — defensivo: marca não-playable em vez de crash.
      return {
        isPlayable: false,
        reason: `weeklySchedule[${todayCode}] inválido: ${(err as Error).message}`,
      };
    }
  }

  // SIMPLE mode (default, includes legacy content sem mode definido)
  // — usa daysOfWeek (lowercase) + startAt/endAt.
  const daysRaw = item.daysOfWeek;
  const startAt = item.startAt;
  const endAt = item.endAt;

  // Sem nenhum filtro → sempre toca (legacy preservado).
  const hasDayFilter = daysRaw && daysRaw.length > 0;
  const hasTimeFilter =
    !!startAt && !!endAt && !(startAt === '00:00' && endAt === '23:59');

  if (!hasDayFilter && !hasTimeFilter) {
    return { isPlayable: true, reason: null };
  }

  const { hours, minutes, dayOfWeek } = getCurrentTimeInTimezone(
    DEFAULT_TZ,
    now,
  );

  // Day-of-week check
  if (hasDayFilter) {
    const dayIndices = daysRaw!
      .map((d) => WEEKDAY_CODE_TO_INDEX[normalizeWeekdayCode(d)])
      .filter((idx) => idx !== undefined);
    if (!isDayActive(dayOfWeek, dayIndices)) {
      const todayCode = WEEKDAY_INDEX_TO_CODE[dayOfWeek];
      return {
        isPlayable: false,
        reason: `dia ${todayCode} ∉ daysOfWeek=[${daysRaw!.join(',')}]`,
      };
    }
  }

  // Time-of-day check
  if (hasTimeFilter) {
    try {
      const inRange = isTimeInRange({ hours, minutes }, startAt!, endAt!);
      if (!inRange) {
        return {
          isPlayable: false,
          reason: `hora ${hours}:${minutes} fora de ${startAt}-${endAt}`,
        };
      }
    } catch (err) {
      return {
        isPlayable: false,
        reason: `startAt/endAt inválido: ${(err as Error).message}`,
      };
    }
  }

  return { isPlayable: true, reason: null };
}
